/**
 * Provides the `throwConfetti` utility function for creating a celebratory confetti animation, and its associated `ThrowConfettiFireOptions` type.
 * @module throwConfetti
 */
// Import the confetti library
import * as confettiNamespace from 'canvas-confetti';
const confetti = confettiNamespace.default || confettiNamespace; // eslint-disable-line @typescript-eslint/no-explicit-any
/**
 * Base number of particles used for calculating burst size.
 * @internal
 */
const BASE_PARTICLE_COUNT = 200;
/**
 * Default options for each confetti burst, primarily setting the origin point.
 * @internal
 */
const DEFAULT_CONFETTI_OPTIONS = {
    origin: { y: 0.7 }, // Fires confetti from slightly above the bottom-center of the viewport
};
/**
 * Fires a single burst of confetti with specified parameters, merged with defaults.
 * This is an internal helper function for `throwConfetti`.
 *
 * @param {number} particleRatio - A ratio (e.g., 0.25) multiplied by `BASE_PARTICLE_COUNT` (200) to determine the number of particles for this specific burst.
 * @param {ThrowConfettiFireOptions} opts - Configuration options specific to this confetti burst, such as `spread`, `startVelocity`, `decay`, and `scalar`. These override any similar properties in `DEFAULT_CONFETTI_OPTIONS` but are themselves merged with it.
 * @internal
 */
function fire(particleRatio, opts) {
    // Calculate the actual number of particles for this burst.
    const particleCount = Math.floor(BASE_PARTICLE_COUNT * particleRatio);
    // Merge default confetti options with burst-specific options and calculated particle count.
    // `opts` will override any conflicting properties in `DEFAULT_CONFETTI_OPTIONS`.
    confetti({
        ...DEFAULT_CONFETTI_OPTIONS,
        ...opts,
        particleCount,
    });
}
;
/**
 * Creates a celebratory confetti animation by orchestrating multiple bursts of confetti with varying patterns and visual characteristics.
 *
 * **Behavior:**
 * This function triggers a sequence of five distinct confetti bursts, each configured with different parameters for `spread`, `startVelocity`, `decay`, and `scalar`.
 * This creates a more dynamic and visually appealing effect than a single burst.
 * The confetti originates from a default position slightly above the bottom-center of the viewport.
 *
 * The sequence is:
 * 1.  A focused burst with moderate velocity.
 * 2.  A wider spread burst.
 * 3.  A very wide spread burst with slower decay and smaller particles.
 * 4.  Another very wide spread burst with lower start velocity, slower decay, and larger particles.
 * 5.  A final wide spread burst with higher start velocity.
 *
 * This function does not take any parameters to customize the overall effect; it executes a predefined animation sequence.
 *
 * **Demo:**
 *
 * <video controls width="100%" style="max-width: 100%;">
 * <source src="https://design.uncannycloud.com/wp-content/uploads/2025/05/docs__throw-confetti-demo.mp4" type="video/mp4">
 * </video>
 *
 * @since 6.5 Initial implementation.
 *
 * @return {void} This function does not return a value; its effect is purely visual.
 *
 * @example Importing the throwConfetti animation
 * ```typescript
 * // Import the throwConfetti utility
 * import { throwConfetti } from '@automator/shared/animations/throw-confetti';
 * ```
 *
 * @example Triggering the confetti celebration
 * Simply call the function to start the animation.
 * ```typescript
 * throwConfetti();
 * ```
 *
 * @remarks
 * - This function relies on the `canvas-confetti` library.
 * - The visual appearance is determined by the hardcoded parameters in the multiple `fire` calls.
 * - It's a fire-and-forget animation; there's no direct control to stop or modify it once initiated.
 */
export function throwConfetti() {
    // Fire multiple confetti bursts with different configurations for a richer effect.
    fire(0.25, {
        spread: 26,
        startVelocity: 55,
    });
    fire(0.2, {
        spread: 60,
    });
    fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
    });
    fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
    });
    fire(0.1, {
        spread: 120,
        startVelocity: 45,
    });
}
